import { useEffect, useState } from "react";
import {
    createItineraryTemplateDev,
    updateItineraryTemplateDev,
    fetchFlyerCategoryOptions,
    type FlyerCategoryOption,
    type ItineraryTemplateInit,
    type TemplateFieldDef,
} from "./itineraryTemplateService";
import TemplateFieldsEditor from "../common/TemplateFieldsEditor";
import styles from "../common/FlyerTemplateDevForm.module.css";

interface Props {
    onCreated: () => void;
    onError: (msg: string) => void;
    initialTemplate?: ItineraryTemplateInit | null;
    onCancelEdit?: () => void;
}

function useInitState<T>(init: () => T, dep: unknown): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [v, setV] = useState<T>(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setV(init()); }, [dep]);
    return [v, setV];
}

function validateFields(fields: TemplateFieldDef[]): string | null {
    if (fields.length === 0) return "Add at least one form field.";
    const keys = new Set<string>();
    for (const f of fields) {
        if (!f.label.trim()) return "Each field needs a label.";
        if (!f.key.trim()) return "Each field needs a key.";
        if (keys.has(f.key)) return `Duplicate field key: ${f.key}`;
        keys.add(f.key);
    }
    return null;
}

const sectionHeader = (label: string) => (
    <div style={{
        display: "flex", alignItems: "center", gap: "0.5rem",
        borderTop: "1px solid rgba(148,163,184,0.12)",
        paddingTop: "0.75rem", marginTop: "0.25rem",
    }}>
        <span style={{
            fontSize: "0.6875rem", fontWeight: 700, color: "#94a3b8",
            textTransform: "uppercase", letterSpacing: "0.05em",
        }}>{label}</span>
    </div>
);

export default function ItineraryTemplateDevForm({ onCreated, onError, initialTemplate, onCancelEdit }: Props) {
    const isEdit = Boolean(initialTemplate);
    const [categories, setCategories] = useState<FlyerCategoryOption[]>([]);
    const [open, setOpen] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // File uploads
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [samplePdfFile, setSamplePdfFile] = useState<File | null>(null);
    const [htmlTemplateFile, setHtmlTemplateFile] = useState<File | null>(null);

    const [extractionNote, setExtractionNote] = useState<string>("");

    // Core fields
    const [categorySlug, setCategorySlug] = useInitState(() => initialTemplate?.category ?? "", initialTemplate?.id);
    const [name, setName] = useInitState(() => initialTemplate?.name ?? "", initialTemplate?.id);
    const [basePrompt, setBasePrompt] = useInitState(() => initialTemplate?.base_prompt ?? "", initialTemplate?.id);
    const [fields, setFields] = useInitState<TemplateFieldDef[]>(() => (initialTemplate as { fields?: TemplateFieldDef[] })?.fields ?? [], initialTemplate?.id);
    const [htmlTemplateId, setHtmlTemplateId] = useInitState(() => (initialTemplate as { html_template_id?: string })?.html_template_id ?? "", initialTemplate?.id);
    const [htmlTemplateContent, setHtmlTemplateContent] = useInitState(() => (initialTemplate as { html_template_content?: string })?.html_template_content ?? "", initialTemplate?.id);
    const [tagline, setTagline] = useInitState(() => (initialTemplate as { tagline?: string })?.tagline ?? "", initialTemplate?.id);
    const [ctaText, setCtaText] = useInitState(() => (initialTemplate as { cta_text?: string })?.cta_text ?? "Book Now", initialTemplate?.id);

    useEffect(() => {
        if (isEdit) setOpen(true);
    }, [isEdit, initialTemplate?.id]);

    useEffect(() => {
        let cancelled = false;
        void fetchFlyerCategoryOptions()
            .then((data: FlyerCategoryOption[]) => { if (!cancelled) setCategories(data); })
            .catch(() => { if (!cancelled) onError("Could not load category list"); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async () => {
        if (!name.trim()) { onError("Template name is required."); return; }

        // base_prompt required only when no sample PDF is supplied
        if (!samplePdfFile && !basePrompt.trim()) {
            onError("Either upload a sample PDF (auto-extracts base prompt) or fill in the Base Prompt manually.");
            return;
        }

        // preview required only when no sample PDF (first page auto-used)
        if (!isEdit && !previewFile && !samplePdfFile) {
            onError("Either upload a sample PDF (auto-thumbnail) or supply a preview image.");
            return;
        }

        // Only validate fields when user filled them manually (PDF auto-detects)
        if (!samplePdfFile) {
            const fieldErr = validateFields(fields);
            if (fieldErr) { onError(fieldErr); return; }
        }

        setSubmitting(true);
        setExtractionNote("");
        try {
            const payload = {
                category_slug: categorySlug || "itinerary",
                name: name.trim(),
                base_prompt: basePrompt.trim(),
                fields,
                html_template_id: htmlTemplateId.trim(),
                html_template_content: htmlTemplateContent,
                tagline: tagline.trim(),
                cta_text: ctaText,
                preview_image: previewFile ?? undefined,
                sample_pdf: samplePdfFile ?? undefined,
                html_template_file: htmlTemplateFile ?? undefined,
            };

            if (isEdit && initialTemplate) {
                await updateItineraryTemplateDev(initialTemplate.id, payload);
            } else {
                const result = await createItineraryTemplateDev(payload);
                if (result.message.includes("auto-extracted")) {
                    setExtractionNote(result.message);
                }
                setName(""); setBasePrompt(""); setTagline(""); setHtmlTemplateId(""); setHtmlTemplateContent("");
                setFields([]); setPreviewFile(null); setSamplePdfFile(null); setHtmlTemplateFile(null);
            }
            onCreated();
        } catch (e) {
            onError(e instanceof Error ? e.message : "Failed to save template.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className={styles.panel}
            aria-label={isEdit ? `Edit itinerary template: ${initialTemplate?.name ?? ""}` : "Create itinerary template"}
            style={{
                borderColor: isEdit ? "rgba(251,191,36,0.4)" : "rgba(249,115,22,0.35)",
                background: isEdit
                    ? "linear-gradient(145deg, rgba(251,191,36,0.07) 0%, rgba(15,23,42,0.4) 100%)"
                    : "linear-gradient(145deg, rgba(249,115,22,0.08) 0%, rgba(15,23,42,0.4) 100%)",
            }}
        >
            {/* ── Toggle header ── */}
            <button
                type="button"
                className={styles.toggle}
                style={{ color: "#fdba74" }}
                onClick={() => setOpen(v => !v)}
            >
                <span>{isEdit ? `Editing: ${initialTemplate?.name ?? "template"}` : "Create itinerary template"}</span>
                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                    {isEdit && onCancelEdit && (
                        <span
                            className={styles.badge}
                            style={{ background: "rgba(239,68,68,0.2)", color: "#fca5a5", cursor: "pointer" }}
                            onClick={e => { e.stopPropagation(); onCancelEdit(); }}
                        >
                            Cancel edit
                        </span>
                    )}
                    <span className={styles.badge} style={isEdit ? { background: "rgba(251,191,36,0.2)", color: "#fcd34d" } : { background: "rgba(249,115,22,0.2)", color: "#fdba74" }}>
                        {isEdit ? "Edit" : "Development"}
                    </span>
                    <span className={styles.badge} style={{ background: "rgba(249,115,22,0.2)", color: "#fdba74" }}>
                        🗺️ Itinerary
                    </span>
                </div>
            </button>

            {open && (
                <div className={styles.body}>
                    {!isEdit && (
                        <p className={styles.hint}>
                            Creates a Jinja2-based itinerary template. <strong>Quick mode:</strong> upload a sample PDF —
                            the LLM auto-extracts the base prompt and form fields. Upload a <code>.html</code> Jinja2 file
                            for the visual layout. Fill fields manually to override anything.
                        </p>
                    )}
                    {isEdit && (
                        <p className={styles.hint}>
                            Upload a new <code>.html</code> to update the layout (e.g. to render new fields you add below).
                            Upload a new sample PDF to re-extract the base prompt and fields. Leave any file blank to keep existing.
                        </p>
                    )}

                    {/* ── 0. Sample PDF (auto-extract) ── */}
                    {sectionHeader("0 · Sample PDF — LLM auto-extracts base prompt & fields (optional)")}
                    <div className={styles.field}>
                        <label htmlFor="it-pdf">
                            {isEdit ? "Re-upload sample itinerary PDF" : "Upload sample itinerary PDF"}
                            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>
                                {isEdit
                                    ? "(re-extracts base prompt + fields)"
                                    : "(auto-fills base prompt, fields + preview thumbnail)"}
                            </span>
                        </label>
                        <input
                            id="it-pdf"
                            type="file"
                            accept="application/pdf,.pdf"
                            className={styles.fileInput}
                            onChange={e => {
                                const f = e.target.files?.[0] ?? null;
                                setSamplePdfFile(f);
                                if (f) setExtractionNote("");
                            }}
                        />
                        {samplePdfFile && (
                            <p style={{ fontSize: "0.75rem", color: "#86efac", marginTop: 4 }}>
                                ✓ {samplePdfFile.name} — base prompt and fields will be auto-extracted. You can override below.
                            </p>
                        )}
                    </div>
                    {extractionNote && (
                        <div style={{
                            fontSize: "0.75rem", color: "#86efac",
                            background: "rgba(134,239,172,0.08)",
                            border: "1px solid rgba(134,239,172,0.2)",
                            borderRadius: 6, padding: "0.5rem 0.75rem",
                        }}>
                            ✓ {extractionNote}
                        </div>
                    )}

                    {/* ── 1. Identity ── */}
                    {sectionHeader("1 · Identity")}
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="it-category">Category</label>
                            <select
                                id="it-category"
                                className={styles.select}
                                value={categorySlug}
                                onChange={e => setCategorySlug(e.target.value)}
                            >
                                <option value="">Select subcategory…</option>
                                {categories.map(c => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.name} — {c.display_group}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="it-name">Template name</label>
                            <input
                                id="it-name"
                                className={styles.input}
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. Maldives 7-Night Luxury Itinerary"
                            />
                        </div>
                    </div>

                    {/* ── 2. Jinja2 HTML layout ── */}
                    {sectionHeader("2 · Jinja2 HTML layout template")}
                    <p className={styles.hint}>
                        The Jinja2 HTML below controls the visual layout of generated itineraries. It is
                        stored in MongoDB and is the <strong>primary render source</strong> — edit it here directly,
                        or upload a <code>.html</code> file to load its content into the editor (the file is also
                        mirrored to <code>templates/itinerary/</code> on the server).
                        Leave the editor blank to fall back to the default <code>bespoke_luxe</code> layout.
                        To add new fields to the output, reference them with{" "}
                        <code>{"{{ data.get('your_field') }}"}</code> and add the field in section 4 below.
                    </p>
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="it-html-file">
                                {isEdit ? "Replace layout HTML" : "Upload layout HTML"}
                                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>(Jinja2 .html)</span>
                            </label>
                            <input
                                id="it-html-file"
                                type="file"
                                accept="text/html,.html"
                                className={styles.fileInput}
                                onChange={e => {
                                    const f = e.target.files?.[0] ?? null;
                                    setHtmlTemplateFile(f);
                                    if (f && !htmlTemplateId.trim()) {
                                        // Auto-populate ID from filename
                                        const derived = f.name.replace(/\.html$/i, "")
                                            .toLowerCase().replace(/[^a-z0-9_-]/g, "_").replace(/^_+|_+$/g, "");
                                        setHtmlTemplateId(derived);
                                    }
                                    // Load the file's text into the editor so it can be reviewed/edited before saving.
                                    if (f) void f.text().then(setHtmlTemplateContent);
                                }}
                            />
                            {htmlTemplateFile && (
                                <p style={{ fontSize: "0.75rem", color: "#86efac", marginTop: 4 }}>
                                    ✓ {htmlTemplateFile.name} — will be deployed as <code>{htmlTemplateId || "auto"}.html</code>
                                </p>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="it-html-id">
                                Template ID
                                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>(without .html)</span>
                            </label>
                            <input
                                id="it-html-id"
                                className={styles.input}
                                value={htmlTemplateId}
                                onChange={e => setHtmlTemplateId(e.target.value)}
                                placeholder="e.g. bespoke_luxe, imperial_luxe"
                            />
                            <p style={{ fontSize: "0.7rem", color: "#64748b", marginTop: 3 }}>
                                Auto-derived from filename. Only set manually if you want to override an existing template file.
                            </p>
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="it-html-content">
                            Layout HTML (stored in MongoDB)
                            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>
                                {htmlTemplateContent ? `${htmlTemplateContent.length.toLocaleString()} chars` : "(blank → bespoke_luxe fallback)"}
                            </span>
                        </label>
                        <textarea
                            id="it-html-content"
                            className={styles.textarea}
                            rows={12}
                            spellCheck={false}
                            value={htmlTemplateContent}
                            onChange={e => setHtmlTemplateContent(e.target.value)}
                            placeholder="<!DOCTYPE html>&#10;<html>… your Jinja2 itinerary layout …</html>"
                            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.75rem", whiteSpace: "pre" }}
                        />
                    </div>

                    {/* ── 3. Base prompt ── */}
                    {sectionHeader("3 · Base prompt — LLM content-generation instructions")}
                    <div className={styles.field}>
                        <label htmlFor="it-prompt">
                            Base prompt
                            {samplePdfFile && (
                                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>
                                    (auto-extracted from PDF if left blank)
                                </span>
                            )}
                        </label>
                        <p className={styles.promptHelp}>
                            Instructions for the LLM when generating itinerary content — tone, level of detail,
                            which sections to include, pricing style, visa notes, etc. This is permanent per-template DNA.
                            The Jinja2 HTML controls visual layout; this controls the content.
                        </p>
                        <textarea
                            id="it-prompt"
                            className={styles.textarea}
                            rows={4}
                            value={basePrompt}
                            onChange={e => setBasePrompt(e.target.value)}
                            placeholder="Generate a premium luxury itinerary with detailed day-wise plans. Include hotel names, restaurant recommendations, activity timings, and insider tips. Add pricing tiers (twin sharing / triple). Include flight details and visa requirements."
                        />
                    </div>

                    {/* ── 4. User form fields ── */}
                    {sectionHeader("4 · User input fields")}
                    <p className={styles.hint}>
                        Fields the user fills in (or the LLM collects via chat). These are passed to the Jinja2 template
                        as <code>data.get('field_key')</code>. To render a new field visually, add it here{" "}
                        <em>and</em> update the HTML layout (section 2) with{" "}
                        <code>{"{{ data.get('field_key') }}"}</code>.
                        Common keys: <code>destination</code>, <code>duration</code>, <code>departure_city</code>,{" "}
                        <code>hotels</code>, <code>day_wise_itinerary</code>, <code>inclusions</code>, <code>exclusions</code>,{" "}
                        <code>starting_price</code>, <code>group_size</code>, <code>travel_dates</code>.
                    </p>
                    <TemplateFieldsEditor fields={fields} onChange={setFields} />

                    {/* ── 5. CTA & metadata ── */}
                    {sectionHeader("5 · CTA & metadata")}
                    <div className={styles.grid2}>
                        <div className={styles.field}>
                            <label htmlFor="it-tagline">Tagline</label>
                            <input
                                id="it-tagline"
                                className={styles.input}
                                value={tagline}
                                onChange={e => setTagline(e.target.value)}
                                placeholder="e.g. Where Love Meets the Ocean"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="it-cta">CTA text</label>
                            <input
                                id="it-cta"
                                className={styles.input}
                                value={ctaText}
                                onChange={e => setCtaText(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* ── 6. Preview thumbnail ── */}
                    {sectionHeader("6 · Preview thumbnail")}
                    <div className={styles.field}>
                        <label htmlFor="it-preview">
                            Cover thumbnail{" "}
                            {isEdit
                                ? <span style={{ fontWeight: 400, color: "#64748b" }}>(leave blank to keep existing)</span>
                                : samplePdfFile
                                    ? <span style={{ fontWeight: 400, color: "#64748b" }}>(optional — PDF first page used if blank)</span>
                                    : null
                            }
                        </label>
                        {isEdit && (initialTemplate as { preview_image_url?: string })?.preview_image_url && !previewFile && (
                            <img
                                src={(initialTemplate as { preview_image_url?: string }).preview_image_url}
                                alt="Current preview"
                                style={{ height: 80, borderRadius: 6, marginBottom: 6, objectFit: "cover", display: "block" }}
                            />
                        )}
                        <input
                            id="it-preview"
                            type="file"
                            accept="image/*"
                            className={styles.fileInput}
                            onChange={e => setPreviewFile(e.target.files?.[0] ?? null)}
                        />
                    </div>

                    {/* ── Submit ── */}
                    <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                        <button
                            type="button"
                            style={{
                                padding: "0.55rem 1.25rem",
                                borderRadius: 8,
                                border: "none",
                                background: "linear-gradient(135deg, #c2410c 0%, #f97316 100%)",
                                color: "#fff7ed",
                                fontSize: "0.8125rem",
                                fontWeight: 700,
                                cursor: submitting ? "wait" : "pointer",
                                opacity: submitting ? 0.55 : 1,
                            }}
                            disabled={submitting}
                            onClick={() => void handleSubmit()}
                        >
                            {submitting ? "Saving…" : isEdit ? "Update itinerary template" : "Save itinerary template"}
                        </button>
                        {isEdit && onCancelEdit && (
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.3)", borderRadius: 8, padding: "0.5rem 1rem", color: "#94a3b8", fontSize: "0.8125rem", cursor: "pointer" }}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
